






















































































import { computed, defineComponent, onMounted, PropType, ref } from "@vue/composition-api";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { EventPostStatus } from "@scrile/api-provider/dist/projects/scrileConnect/EventPostProvider";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import { isMediaFile } from "@/lib/fileHelpers";
import { getLiveUrl, getProfileUrl } from "@/lib/urlHelpers";
import router from "@/router";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseIcon from "@/components/BaseIcon";
import { ProcessedContentFiles } from "@/components/ContentFeedItem";
import ContentFeedBaseView from "@/components/ContentFeedItem/components/ContentFeedBaseView";
import ContentFeedEventTicket from "@/components/ContentFeedItem/components/ContentFeedEventTicket";
import ContentFeedFileList from "@/components/ContentFeedItem/components/ContentFeedFileList";
import ContentFeedGallery from "@/components/ContentFeedItem/components/ContentFeedGallery";
import EventTicketPurchaseModal from "@/components/EventTicketPurchaseModal";
import FormatMoney from "@/components/FormatMoney";

export default defineComponent({
  name: "EventView",
  components: {
    EventTicketPurchaseModal,
    BaseIcon,
    BaseButton,
    ContentFeedBaseView,
    ContentFeedFileList,
    ContentFeedGallery,
    ContentFeedEventTicket,
    BaseConfirm,
    FormatMoney,
  },
  props: {
    contentFeed: { type: Object as () => ContentFeed, required: true },
    subscriptionLevels: { type: Array as () => SubscriptionLevel[], required: true },
    paymentPackages: { type: Array as () => PaymentPackage[], required: true },
    showFollowButton: { type: Boolean, required: false, default: false },
    showReportItem: { type: Boolean, required: false, default: true },
    showComment: { type: Boolean, required: false, default: true },
    showFooter: { type: Boolean, required: false, default: true },
    pinnedPostId: { type: String as PropType<string | null>, default: null },
    enablePinPost: { type: Boolean, default: false },
    isFollowedUser: { type: Boolean },
  },
  setup(props) {
    const showPurchaseModal = ref(false);
    const isTicketBooked = ref(false);
    const { authUser, authUserStrict } = useAuthUser();

    const isMyEvent = computed(() => {
      return !!authUser && authUser.value?.id === props.contentFeed.userId;
    });
    const hasAccessToFile = (f: ContentFile) => {
      return isMyEvent.value || props.contentFeed.purchased || f.publicAccess;
    };

    const ticketsBooked = computed(() => {
      return props.contentFeed.details?.event.ticketsBooked || 0;
    });

    const processedFiles = computed(() => {
      return props.contentFeed.files.reduce<{ documents: ProcessedContentFiles; media: ProcessedContentFiles }>(
        (out, f) => {
          const key = isMediaFile(f) ? "media" : "documents";
          const accessKey = hasAccessToFile(f) ? "accessed" : "locked";
          out[key].all.push(f);
          out[key][accessKey].push(f);
          return out;
        },
        {
          documents: { locked: [], accessed: [], all: [] },
          media: { locked: [], accessed: [], all: [] },
        }
      );
    });

    const showBookBlock = computed(() => {
      return (
        props.contentFeed.details?.event.minimumLevelId !== "0" ||
        props.contentFeed.details?.event.ticketPricePackageId !== "0"
      );
    });

    const liveUrl = computed(() => {
      return getLiveUrl(authUserStrict.value);
    });
    const feedRoute = getProfileUrl(props.contentFeed.user, { feedId: props.contentFeed.id });
    const redirectToFeed = async () => {
      await router.push(feedRoute);
    };

    const onBookTicketClick = useAuthorisedOnly(
      () => {
        showPurchaseModal.value = true;
      },
      { onLoginSuccess: redirectToFeed, onSignUpSuccess: redirectToFeed }
    );

    const showStartBtn = computed(() => {
      return (
        (props.contentFeed.details?.event?.status === EventPostStatus.READY_TO_START ||
          props.contentFeed.details?.event?.status === EventPostStatus.STARTED) &&
        isMyEvent.value
      );
    });

    const showBookedBtn = computed(() => {
      return props.contentFeed.details?.event?.status === EventPostStatus.PUBLISHED && isMyEvent.value;
    });

    const showTicketBookedBtn = computed(() => {
      return (
        (props.contentFeed.details?.event?.status === EventPostStatus.PUBLISHED ||
          props.contentFeed.details?.event?.status === EventPostStatus.READY_TO_START ||
          props.contentFeed.details?.event?.status === EventPostStatus.STARTED) &&
        showBookBlock.value &&
        !isMyEvent.value
      );
    });

    const showEndedText = computed(() => {
      return props.contentFeed.details?.event?.status === EventPostStatus.ENDED;
    });

    const showCancelledText = computed(() => {
      return props.contentFeed.details?.event?.status === EventPostStatus.CANCELLED;
    });

    const goLive = () => {
      router.push(liveUrl.value);
    };

    const finishBooking = () => {
      showPurchaseModal.value = false;
      isTicketBooked.value = true;
    };

    onMounted(() => {
      isTicketBooked.value = props.contentFeed.details?.event.availableByTicket;
    });

    return {
      isMyEvent,
      showStartBtn,
      showBookedBtn,
      showEndedText,
      ticketsBooked,
      processedFiles,
      isTicketBooked,
      showCancelledText,
      showPurchaseModal,
      onBookTicketClick,
      showTicketBookedBtn,
      goLive,
      finishBooking,
    };
  },
});
